/**
 * HRAPI.js
 * Shared API client for all HR screens
 * Base URL: https://api.jobeng.ai
 *
 * UPDATED: Only includes endpoints verified working from tests
 */

import AsyncStorage from '@react-native-async-storage/async-storage';

// ─── API Client ──────────────────────────────────────────────────────────────
const BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

// Generic API caller with cache/fallback
const api = async (endpoint, method = 'GET', body = null) => {
  try {
    const options = {
      method,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'x-api-key': API_KEY,
      },
    };

    if (body) {
      options.body = JSON.stringify(body);
    }

    const response = await fetch(`${BASE}${endpoint}`, options);
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'API request failed');
    }
    
    return { success: true, data: data.data || data };
  } catch (error) {
    console.warn(`[HRAPI] ${method} ${endpoint} failed:`, error.message);
    return { success: false, data: null, error: error.message };
  }
};

// ─── CACHE KEYS ──────────────────────────────────────────────────────────────
export const HR_CACHE_KEYS = {
  EMPLOYEES: 'hr_employees',
  EMPLOYEE: (id) => `hr_employee_${id}`,
  LEAVE_REQUESTS: 'hr_leave_requests',
  LEAVE_REQUEST: (id) => `hr_leave_request_${id}`,
  PAYROLL_EMPLOYEES: 'hr_payroll_employees',
  PAYROLL_RUNS: 'hr_payroll_runs',
  PAYSLIPS: 'hr_payslips',
  PERFORMANCE_REVIEWS: 'hr_performance_reviews',
  PERFORMANCE_REVIEW: (id) => `hr_performance_review_${id}`,
  WARNING_LETTERS: 'hr_warning_letters',
  WARNING_LETTER: (id) => `hr_warning_letter_${id}`,
};

// ─── HR API - Only Verified Working Endpoints ───────────────────────────────
export const HRAPI = {
  // ===== EMPLOYEES (VERIFIED WORKING) =====
  getEmployees: (companyId = 1) => 
    api(`/api/data/employees?company_id=${companyId}`),
  
  getEmployee: (id) => 
    api(`/api/data/employees/${id}`),
  
  addEmployee: (data) => 
    api('/api/data/employees', 'POST', { ...data, company_id: data.company_id || 1 }),
  
  updateEmployee: (id, data) => 
    api(`/api/data/employees/${id}`, 'PUT', data),
  
  deleteEmployee: (id) => 
    api(`/api/data/employees/${id}`, 'DELETE'),

  searchEmployees: (query, companyId = 1) => 
    api(`/api/data/employees?company_id=${companyId}&search=${encodeURIComponent(query)}`),

  // ===== LEAVE MANAGEMENT (VERIFIED WORKING) =====
  getLeaveRequests: (companyId = 1) => 
    api(`/api/data/leave_requests?company_id=${companyId}`),

  getLeaveRequest: (id) => 
    api(`/api/data/leave_requests/${id}`),

  createLeaveRequest: (data) => 
    api('/api/data/leave_requests', 'POST', { 
      ...data, 
      status: 'pending',
      created_at: new Date().toISOString() 
    }),

  updateLeaveRequest: (id, data) => 
    api(`/api/data/leave_requests/${id}`, 'PUT', data),

  deleteLeaveRequest: (id) => 
    api(`/api/data/leave_requests/${id}`, 'DELETE'),

  approveLeave: (id, status, note) => 
    api(`/api/data/leave_requests/${id}`, 'PUT', { 
      status, 
      note, 
      reviewed_at: new Date().toISOString() 
    }),

  // ===== PAYROLL (VERIFIED WORKING) =====
  getPayrollEmployees: (companyId = 1) => 
    api(`/api/data/payroll_employees?company_id=${companyId}`),

  getPayrollRuns: (companyId = 1) => 
    api(`/api/data/payroll_runs?company_id=${companyId}`),

  getPayslips: (employeeId = null, companyId = 1) => {
    const params = employeeId 
      ? `?employee_id=${employeeId}` 
      : `?company_id=${companyId}`;
    return api(`/api/data/payslips${params}`);
  },

  // ===== PERFORMANCE REVIEWS (VERIFIED WORKING) =====
  getPerformanceReviews: (companyId = 1) => 
    api(`/api/data/employee_performance_reviews?company_id=${companyId}`),

  getPerformanceReview: (id) => 
    api(`/api/data/employee_performance_reviews/${id}`),

  createPerformanceReview: (data) => 
    api('/api/data/employee_performance_reviews', 'POST', { 
      ...data, 
      created_at: new Date().toISOString(),
      status: 'Draft' // Note: Uses enum from DB
    }),

  updatePerformanceReview: (id, data) => 
    api(`/api/data/employee_performance_reviews/${id}`, 'PUT', data),

  deletePerformanceReview: (id) => 
    api(`/api/data/employee_performance_reviews/${id}`, 'DELETE'),

  // ===== WARNING LETTERS (VERIFIED WORKING) =====
  getWarningLetters: (companyId = 1) => 
    api(`/api/data/warning_letters?company_id=${companyId}`),

  getWarningLetter: (id) => 
    api(`/api/data/warning_letters/${id}`),

  issueWarning: (data) => 
    api('/api/data/warning_letters', 'POST', { 
      ...data, 
      issued_date: new Date().toISOString().split('T')[0], // Date only
      status: 'Active'
    }),

  updateWarning: (id, data) => 
    api(`/api/data/warning_letters/${id}`, 'PUT', data),

  // ===== EMPLOYEE DASHBOARD (VERIFIED WORKING) =====
  getEmployeeDashboard: (employeeId) => 
    api(`/api/employee/dashboard/${employeeId}`),

  // ===== CUSTOM PAYROLL ENDPOINTS (VERIFIED WORKING) =====
  getPayrollEmployeesCustom: () => 
    api('/api/payroll/employees'),

  getPayrollRunsCustom: () => 
    api('/api/payroll/runs'),

  getTaxBands: () => 
    api('/api/payroll/tax-bands/current'),

  getEmployeePayslips: (employeeId) => 
    api(`/api/payroll/employee/${employeeId}/payslips`),

  getEmployeeYTD: (employeeId) => 
    api(`/api/payroll/employee/${employeeId}/ytd`),

  // ===== OVERVIEW (Calculated from existing data) =====
  getOverview: async (companyId = 1) => {
    try {
      const [employees, leaveRequests, warningLetters] = await Promise.all([
        HRAPI.getEmployees(companyId),
        HRAPI.getLeaveRequests(companyId),
        HRAPI.getWarningLetters(companyId)
      ]);

      const employeeData = employees.success ? employees.data || [] : [];
      const leaveData = leaveRequests.success ? leaveRequests.data || [] : [];
      const warningData = warningLetters.success ? warningLetters.data || [] : [];

      const activeEmployees = employeeData.filter(e => 
        e.status === 'Active' || e.status === 'active'
      ).length;
      
      const pendingLeave = leaveData.filter(l => 
        l.status === 'pending'
      ).length;
      
      const activeWarnings = warningData.filter(w => 
        w.status === 'Active'
      ).length;

      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const newHires = employeeData.filter(e => {
        const hireDate = new Date(e.hire_date || e.start_date);
        return hireDate >= thirtyDaysAgo;
      }).length;

      return {
        success: true,
        data: {
          totalEmployees: employeeData.length,
          activeEmployees,
          onLeave: pendingLeave,
          pendingReviews: 0, // Could be calculated if we add reviews query
          newHires,
          turnoverRate: 0, // Would need historical data
          activeWarnings,
        }
      };
    } catch (error) {
      return {
        success: false,
        data: {
          totalEmployees: 0,
          activeEmployees: 0,
          onLeave: 0,
          pendingReviews: 0,
          newHires: 0,
          turnoverRate: 0,
          activeWarnings: 0,
        }
      };
    }
  },
};

// ─── EXPORT SINGLE API CALLER FOR CUSTOM ENDPOINTS ──────────────────────────
export default HRAPI;